import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  ListTodo, 
  FileText, 
  Calendar, 
  StickyNote,
  Trash2,
  Loader2,
  Check,
  Clock,
  Link2,
  X,
  Save,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getDefaultTasks } from "@/config/actionConfig";
import { TaskDocumentLinkDialog } from "./TaskDocumentLinkDialog";
import { WorkspaceItemType } from "./WorkspaceActionBar";

interface WorkspaceTimelineProps {
  documentId: string;
  docType: string | null;
  activeAddType: WorkspaceItemType | null;
  onCancelAdd: () => void;
  initialReminderDate: string | null;
  initialReminderNote: string | null;
}

interface ActionTask {
  id: string;
  label: string;
  is_completed: boolean;
  linked_document_id: string | null;
  sort_order: number;
  created_at: string;
}

export function WorkspaceTimeline({ 
  documentId, 
  docType, 
  activeAddType,
  onCancelAdd,
  initialReminderDate,
  initialReminderNote
}: WorkspaceTimelineProps) {
  const queryClient = useQueryClient();
  const [newTaskLabel, setNewTaskLabel] = useState("");
  const [newNote, setNewNote] = useState("");
  const [reminderDate, setReminderDate] = useState(initialReminderDate || "");
  const [reminderNote, setReminderNote] = useState("");
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskLabel, setSelectedTaskLabel] = useState("");

  // Fetch tasks
  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['action-tasks', documentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('action_tasks')
        .select('*')
        .eq('document_id', documentId)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as ActionTask[];
    }
  });

  // Fetch linked documents info
  const linkedDocIds = tasks.filter(t => t.linked_document_id).map(t => t.linked_document_id);
  const { data: linkedDocs = [] } = useQuery({
    queryKey: ['linked-docs', linkedDocIds],
    queryFn: async () => {
      if (linkedDocIds.length === 0) return [];
      const { data, error } = await supabase
        .from('documents')
        .select('id, title')
        .in('id', linkedDocIds);
      if (error) throw error;
      return data;
    },
    enabled: linkedDocIds.length > 0
  });

  // Add task mutation
  const addTask = useMutation({
    mutationFn: async (label: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');
      
      const { error } = await supabase
        .from('action_tasks')
        .insert({
          document_id: documentId,
          user_id: user.id,
          label,
          sort_order: tasks.length
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-tasks', documentId] });
      setNewTaskLabel("");
      onCancelAdd();
      toast.success("Tâche ajoutée");
    },
    onError: () => toast.error("Erreur lors de l'ajout")
  });

  // Toggle task
  const toggleTask = useMutation({
    mutationFn: async ({ id, completed }: { id: string; completed: boolean }) => {
      const { error } = await supabase
        .from('action_tasks')
        .update({ is_completed: completed })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-tasks', documentId] });
    }
  });

  // Delete task
  const deleteTask = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('action_tasks')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-tasks', documentId] });
    }
  });

  // Link document to task
  const linkDocument = useMutation({
    mutationFn: async ({ taskId, linkedDocId }: { taskId: string; linkedDocId: string }) => {
      const { error } = await supabase
        .from('action_tasks')
        .update({ 
          linked_document_id: linkedDocId,
          is_completed: true 
        })
        .eq('id', taskId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-tasks', documentId] });
      setLinkDialogOpen(false);
      setSelectedTaskId(null);
      setSelectedTaskLabel("");
      toast.success("Document lié !");
    }
  });

  // Save reminder (appointment)
  const saveReminder = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('documents')
        .update({
          reminder_date: reminderDate || null,
          reminder_note: reminderNote || null
        })
        .eq('id', documentId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-action', documentId] });
      onCancelAdd();
      toast.success("RDV enregistré !");
    },
    onError: () => toast.error("Erreur lors de la sauvegarde")
  });

  // Add default tasks
  const addDefaultTasks = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');
      
      const defaults = getDefaultTasks(docType);
      const existingLabels = tasks.map(t => t.label.toLowerCase());
      const newTasks = defaults.filter(d => !existingLabels.includes(d.toLowerCase()));
      
      if (newTasks.length === 0) {
        toast.info("Toutes les tâches suggérées sont déjà ajoutées");
        return;
      }
      
      const { error } = await supabase
        .from('action_tasks')
        .insert(newTasks.map((label, i) => ({
          document_id: documentId,
          user_id: user.id,
          label,
          sort_order: tasks.length + i
        })));
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-tasks', documentId] });
      toast.success("Tâches suggérées ajoutées !");
    }
  });

  const handleAddTask = () => {
    if (!newTaskLabel.trim()) return;
    addTask.mutate(newTaskLabel.trim());
  };

  const handleLinkClick = (taskId: string, taskLabel: string) => {
    setSelectedTaskId(taskId);
    setSelectedTaskLabel(taskLabel);
    setLinkDialogOpen(true);
  };

  const handleDocumentLinked = (docId: string, _docTitle: string) => {
    if (selectedTaskId) {
      linkDocument.mutate({ taskId: selectedTaskId, linkedDocId: docId });
    }
  };

  const completedCount = tasks.filter(t => t.is_completed).length;
  const progress = tasks.length > 0 ? (completedCount / tasks.length) * 100 : 0;
  const hasDefaultTasks = getDefaultTasks(docType).length > 0;
  const hasAppointment = !!initialReminderDate;

  if (tasksLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isEmpty = tasks.length === 0 && !hasAppointment;

  return (
    <>
      <div className="space-y-3">
        {/* Progress bar */}
        {tasks.length > 0 && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Progression</span>
              <span className="font-medium">{completedCount}/{tasks.length}</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {/* Empty state */}
        {isEmpty && !activeAddType && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <ListTodo className="w-6 h-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">
                Votre espace de travail est vide
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Utilisez les boutons ci-dessus pour organiser votre démarche
              </p>
              {hasDefaultTasks && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => addDefaultTasks.mutate()}
                  disabled={addDefaultTasks.isPending}
                >
                  {addDefaultTasks.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Commencer avec les étapes suggérées
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add forms based on activeAddType */}
        {activeAddType === 'task' && (
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <ListTodo className="w-4 h-4 text-primary shrink-0" />
                <Input
                  autoFocus
                  placeholder="Nouvelle tâche..."
                  value={newTaskLabel}
                  onChange={(e) => setNewTaskLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddTask();
                    if (e.key === 'Escape') onCancelAdd();
                  }}
                  className="flex-1 h-9"
                />
                <Button 
                  size="sm" 
                  className="h-9"
                  onClick={handleAddTask}
                  disabled={!newTaskLabel.trim() || addTask.isPending}
                >
                  {addTask.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-9"
                  onClick={onCancelAdd}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {activeAddType === 'appointment' && (
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Nouveau rendez-vous</span>
              </div>
              <Input
                autoFocus
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                className="w-full"
              />
              <Textarea
                placeholder="Notes (lieu, heure, numéro de dossier...)"
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                className="min-h-[60px] resize-none"
              />
              <div className="flex gap-2">
                <Button 
                  size="sm"
                  className="flex-1 gap-2"
                  onClick={() => saveReminder.mutate()}
                  disabled={!reminderDate || saveReminder.isPending}
                >
                  {saveReminder.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Enregistrer
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={onCancelAdd}
                >
                  Annuler
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {activeAddType === 'note' && (
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <StickyNote className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Nouvelle note</span>
              </div>
              <Textarea
                autoFocus
                placeholder="Votre note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="min-h-[80px] resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') onCancelAdd();
                }}
              />
              <div className="flex gap-2">
                <Button 
                  size="sm"
                  className="flex-1 gap-2"
                  onClick={() => {
                    if (newNote.trim()) {
                      addTask.mutate(`📝 ${newNote.trim()}`);
                      setNewNote("");
                    }
                  }}
                  disabled={!newNote.trim() || addTask.isPending}
                >
                  {addTask.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Ajouter
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={onCancelAdd}
                >
                  Annuler
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Appointment display if exists */}
        {hasAppointment && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Rendez-vous prévu</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(initialReminderDate!), "EEEE d MMMM yyyy", { locale: fr })}
                  </p>
                  {initialReminderNote && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {initialReminderNote}
                    </p>
                  )}
                </div>
                <Clock className="w-4 h-4 text-primary shrink-0" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tasks list */}
        {tasks.map((task) => {
          const linkedDoc = linkedDocs.find(d => d.id === task.linked_document_id);
          const isNote = task.label.startsWith('📝 ');
          const isDocItem = task.label.startsWith('Document : ');
          
          return (
            <Card 
              key={task.id}
              className={cn(
                "transition-all",
                task.is_completed && "bg-success/5 border-success/20"
              )}
            >
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  {/* Icon based on type */}
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mt-0.5",
                    isNote ? "bg-amber-500/10" :
                    isDocItem || linkedDoc ? "bg-blue-500/10" :
                    task.is_completed ? "bg-success/10" : "bg-muted"
                  )}>
                    {isNote ? (
                      <StickyNote className="w-5 h-5 text-amber-500" />
                    ) : isDocItem || linkedDoc ? (
                      <FileText className="w-5 h-5 text-blue-500" />
                    ) : (
                      <Checkbox
                        checked={task.is_completed}
                        onCheckedChange={(checked) => 
                          toggleTask.mutate({ id: task.id, completed: !!checked })
                        }
                        className="w-5 h-5"
                      />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "text-sm",
                      task.is_completed && !isNote && !isDocItem && "line-through text-muted-foreground"
                    )}>
                      {isNote ? task.label.replace('📝 ', '') : 
                       isDocItem ? task.label.replace('Document : ', '') :
                       task.label}
                    </p>
                    {linkedDoc && !isDocItem && (
                      <p className="text-xs text-blue-500 flex items-center gap-1 mt-0.5">
                        <Link2 className="w-3 h-3" />
                        {linkedDoc.title}
                      </p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    {!isNote && !isDocItem && !linkedDoc && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleLinkClick(task.id, task.label)}
                        title="Lier un document"
                      >
                        <Link2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteTask.mutate(task.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Suggest default tasks button */}
        {!isEmpty && hasDefaultTasks && tasks.length < 3 && (
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2 border-dashed"
            onClick={() => addDefaultTasks.mutate()}
            disabled={addDefaultTasks.isPending}
          >
            {addDefaultTasks.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            Ajouter les étapes suggérées
          </Button>
        )}
      </div>

      <TaskDocumentLinkDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        taskLabel={selectedTaskLabel}
        onDocumentLinked={handleDocumentLinked}
      />
    </>
  );
}
